import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import logo from '../assets/logo.png';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
  };

  const menuItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1, duration: 0.3 },
    }),
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col items-center justify-center text-white px-6 py-10 relative"
    >
      <header className="w-full flex justify-between items-center mb-10 px-6">
        <motion.div
          className="flex-1 flex justify-center items-center"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div className="flex flex-col items-center">
            <motion.img
              src={logo}
              alt="Logo Notaría 4 Cantón Otavalo"
              className="w-40 h-auto mb-6"  // Cambiado a h-auto para respetar la proporción
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            />
            <h1 className="text-5xl font-extrabold tracking-tight text-center">
              Software de Auditoría para Notaría
            </h1>
          </motion.div>
        </motion.div>
        <div className="relative z-10">
          <motion.button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-full bg-notaria-blue-2 hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all duration-300 focus:outline-none"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            {isMenuOpen ? (
              <XMarkIcon className="w-8 h-8" />
            ) : (
              <Bars3Icon className="w-8 h-8" />
            )}
          </motion.button>
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                variants={menuVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 mt-2 w-48 bg-notaria-blue-1 bg-opacity-90 rounded-lg shadow-xl py-2"
              >
                <motion.div
                  custom={0}
                  variants={menuItemVariants}
                  className="px-4 py-2 hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all duration-200"
                >
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    Iniciar Sesión
                  </Link>
                </motion.div>
                <motion.div
                  custom={1}
                  variants={menuItemVariants}
                  className="px-4 py-2 hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all duration-200"
                >
                  <Link to="/admin" onClick={() => setIsMenuOpen(false)}>
                    Administración
                  </Link>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>
      <main className="max-w-3xl text-center bg-notaria-blue-1 bg-opacity-80 p-8 rounded-xl shadow-2xl">
        <motion.h2
          className="text-3xl font-semibold mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          Bienvenido al Software de Auditoría
        </motion.h2>
        <motion.p
          className="mb-6 text-lg leading-relaxed"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          Este software está diseñado para optimizar la gestión y auditoría de procesos notariales en la Notaría 4 Cantón Otavalo, ofreciendo una experiencia moderna y dinámica alineada con las últimas tendencias tecnológicas.
        </motion.p>
        <motion.p
          className="font-semibold mb-4 text-lg"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          Funcionalidades principales:
        </motion.p>
        <motion.ul
          className="list-disc list-inside mb-6 text-left text-lg"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <li>Gestión avanzada de usuarios con roles (Notario y Contador).</li>
          <li>Carga y análisis de archivos Excel para trámites diarios.</li>
          <li>Justificación de depósitos con comprobantes detallados.</li>
          <li>Sistema de semáforo para seguimiento de facturas (rojo, amarillo, verde).</li>
          <li>Notificaciones dinámicas estilo popup para alertas en tiempo real.</li>
        </motion.ul>
        <motion.p
          className="mb-8 text-lg leading-relaxed"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          Con un diseño inspirado en el logo de la Notaría, combinamos un gradiente de azules profundos con detalles vibrantes para una interfaz visualmente atractiva y funcional.
        </motion.p>
      </main>
      <footer className="mt-10">
        <p className="text-sm">© 2025 Notaría 4 Cantón Otavalo. Todos los derechos reservados.</p>
      </footer>
    </motion.div>
  );
};

export default Home;